import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Space, Button, Spin, message, theme } from 'antd';
import { EditOutlined, DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/PageHeader';
import { AdminBreadcrumbs } from '../../components/AdminBreadcrumbs';
import { UserStatusTag } from './components/UserStatusTag';
import { DeleteUserConfirmModal } from './components/DeleteUserConfirmModal';
import { ResetUserPasswordAction } from './components/ResetUserPasswordAction';
import { CreateKeycloakUserModal } from './components/CreateKeycloakUserModal';
import { useUser, useDeleteUser } from './api/useUsers';
import { useHasPermission } from '../../auth/useHasPermission';
import { EmptyState } from '../../components/EmptyState';
import { Permission } from '@aid/shared';
import { useT } from '../../locales';
import { useViewport } from '../../lib/useViewport';
import './users.css';

export function UserDetailPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useUser(id!);
  const deleteUser = useDeleteUser();
  const canUpdate = useHasPermission(Permission.USERS_UPDATE);
  const canDelete = useHasPermission(Permission.USERS_DELETE);
  const { token } = theme.useToken();
  const { isMobile } = useViewport();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [createKeycloakOpen, setCreateKeycloakOpen] = useState(false);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <EmptyState
        title={t('pages.users.notFound')}
        description={t('pages.users.notFoundDescription')}
      />
    );
  }

  const handleDelete = async () => {
    try {
      await deleteUser.mutateAsync(user.id);
      message.success(t('pages.users.deactivateSuccess'));
      navigate('/users');
    } catch {
      message.error(t('pages.users.deactivateError'));
    }
  };

  return (
    <div>
      <PageHeader
        className="users-detail-header"
        breadcrumb={<AdminBreadcrumbs current={t('pages.users.detail')} />}
        title={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email}
        subtitle={t('pages.users.userId', { id: user.id })}
        actions={
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            wrap={!isMobile}
            size={[8, 8]}
            style={isMobile ? { width: '100%' } : undefined}
          >
            {canUpdate && user.keycloakId && (
              <ResetUserPasswordAction userId={user.id} block={isMobile} />
            )}
            {canUpdate && !user.keycloakId && (
              <Button
                block={isMobile}
                icon={<UserAddOutlined />}
                onClick={() => setCreateKeycloakOpen(true)}
              >
                {t('pages.users.createKeycloakAction')}
              </Button>
            )}
            {canUpdate && (
              <Button
                block={isMobile}
                icon={<EditOutlined />}
                onClick={() => navigate(`/users/${user.id}/edit`)}
              >
                {t('common.edit')}
              </Button>
            )}
            {canDelete && (
              <Button
                block={isMobile}
                danger
                icon={<DeleteOutlined />}
                onClick={() => setDeleteModalOpen(true)}
              >
                {t('pages.users.deactivate')}
              </Button>
            )}
          </Space>
        }
      />
      <Card
        title={t('pages.users.userInfo')}
        style={{
          marginBottom: 24,
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
        }}
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '14px 22px',
          },
        }}
      >
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          style={{ marginTop: 8 }}
          labelStyle={{
            color: token.colorTextSecondary,
            fontWeight: 500,
            paddingTop: 14,
            paddingBottom: 14,
          }}
          contentStyle={{ paddingTop: 14, paddingBottom: 14 }}
        >
          <Descriptions.Item label={t('pages.users.email')}>{user.email}</Descriptions.Item>
          <Descriptions.Item label={t('pages.users.username')}>{user.username}</Descriptions.Item>
          <Descriptions.Item label={t('pages.users.firstName')}>
            {user.firstName ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('pages.users.lastName')}>
            {user.lastName ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.phone')}>{user.phone ?? '-'}</Descriptions.Item>
          <Descriptions.Item label={t('pages.users.jobTitle')}>
            {user.jobTitle ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('pages.users.department')}>
            {user.department ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('pages.users.status')}>
            <UserStatusTag isActive={user.isActive} />
          </Descriptions.Item>
          <Descriptions.Item label={t('pages.users.createdAt')}>
            {new Date(user.createdAt).toLocaleDateString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('pages.users.updatedAt')}>
            {new Date(user.updatedAt).toLocaleDateString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card
        title={t('pages.users.assignedRoles')}
        style={{
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadowTertiary,
        }}
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '14px 22px',
          },
        }}
      >
        {user.roles.length === 0 ? (
          <EmptyState
            title={t('pages.users.noRoles')}
            description={t('pages.users.noRolesDescription')}
          />
        ) : (
          <Space wrap>
            {user.roles.map((r) => (
              <Tag key={r.id}>{r.name}</Tag>
            ))}
          </Space>
        )}
      </Card>
      <DeleteUserConfirmModal
        open={deleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        loading={deleteUser.isPending}
      />
      <CreateKeycloakUserModal
        user={user}
        open={createKeycloakOpen}
        onClose={() => setCreateKeycloakOpen(false)}
      />
    </div>
  );
}
